import type { AxiosError } from 'axios';
import { getUpdateGeolocationTokenEndpoint } from '@/api/helpers/get-update-geolocation-token-endpoint';
import { getClientIpFromGeolocationToken } from '@/api/gls/get-client-ip';
import {
  createApiAxios,
  getFabricAuthToken,
} from '@/utilities/axios/api-axios';
import { HEADERS } from '@/utilities/constants/metadata';
import { hashValue } from '@/utilities/helpers/hashValue';
import { useStore } from '@/store/useStore';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';

const UPDATE_GEOLOCATION_TOKEN_TIMEOUT_MS = 5000;

type UpdateGeolocationTokenResponse = {
  data?: Record<string, unknown>;
  error?: Record<string, unknown>;
};

/**
 * Pushes a refreshed geolocation JWT to FGP (PUT .../update/geolocationToken).
 *
 * The game-window shell owns the GeoComply flow, so when a fresh token lands it
 * must tell FGP — the port of casino-game-client's `updateGeolocationToken`
 * thunk. Auth/login tokens come from the session store. The request ID and
 * product come from the successful GLS launch so FSM can resolve the allocated
 * game session.
 *
 * Returns `true` on success, `false` when the call is skipped or fails. Never
 * throws — the caller keeps the game running regardless.
 */
export const updateGeolocationToken = async (
  geolocationToken: string,
): Promise<boolean> => {
  const { glsRequestId, launchGeoProduct } = useStore.getState().gameContext;
  const endpoint = getUpdateGeolocationTokenEndpoint();

  if (!geolocationToken || !glsRequestId || !endpoint) {
    return false;
  }

  const instance = createApiAxios();
  const { platform: runtimePlatform, product: runtimeProduct } =
    useStore.getState().runtime;
  // FGP expects DESKTOP | MOBILE; runtime.platform is desktop | xsell | android.
  const platform = runtimePlatform === 'desktop' ? 'DESKTOP' : 'MOBILE';
  const product =
    launchGeoProduct === 'CASINO' || launchGeoProduct === 'POKER'
      ? launchGeoProduct
      : (runtimeProduct || '').toUpperCase();

  Logger.logRumAction({
    name: '[GW][UPDATE_GEOLOCATION_TOKEN] Begin',
    useCase: DdEventCategory.UpdateGeolocation,
    eventType: DdEventType.Info,
  });

  try {
    const authToken = await getFabricAuthToken('UPDATE_GEOLOCATION_TOKEN');

    const { data } = await instance.put<UpdateGeolocationTokenResponse>(
      endpoint,
      {
        requestId: glsRequestId,
        authToken,
        platform,
        ipAddress: getClientIpFromGeolocationToken(geolocationToken),
        geolocationToken,
        product,
      },
      {
        timeout: UPDATE_GEOLOCATION_TOKEN_TIMEOUT_MS,
        // FGP CORS rejects the Cache-Control header createApiAxios adds by
        // default; null removes it (same as fetch-net-position).
        headers: { [HEADERS.CACHE_CONTROL]: null },
      },
    );

    // FGP signals failure in the response body, not the HTTP status.
    if (data?.error) {
      Logger.logRumError({
        name: '[GW][UPDATE_GEOLOCATION_TOKEN] Rejected',
        useCase: DdEventCategory.UpdateGeolocation,
        eventType: DdEventType.Error,
        context: {
          requestId: glsRequestId,
          errorCode: data.error.errorCode,
          errorMessage: data.error.message,
        },
      });
      return false;
    }

    void hashValue(geolocationToken).then((geolocationTokenHash) => {
      Logger.logRumAction({
        name: '[GW][UPDATE_GEOLOCATION_TOKEN] Success',
        useCase: DdEventCategory.UpdateGeolocation,
        eventType: DdEventType.Success,
        context: { geolocationTokenHash },
      });
    });

    return true;
  } catch (error) {
    const axiosError = error as AxiosError<{ errorCode?: string }>;

    Logger.logRumError({
      name: '[GW][UPDATE_GEOLOCATION_TOKEN] Error',
      useCase: DdEventCategory.UpdateGeolocation,
      eventType: DdEventType.Error,
      context: {
        response_code: axiosError?.response?.status ?? 0,
        error_message: axiosError?.response?.data?.errorCode,
      },
    });

    return false;
  }
};
